<template lang="pug">
  div(class="container")
    div(v-if="slider").pb-4
      v-row().px-4
        v-flex(xs12 md12 lg12)
          v-slider(label="Time Scale:" v-model="timeIndex" :tick-labels="timescale" :max="years-1" step="1" ticks="always" tick-size="6" @click="rescale")
      v-row().px-4
        v-flex(xs12 md6 lg6)
          v-slider(label="Group By:" v-model="groupIndex" :tick-labels="groupscale" :max="groupscale.length-1" step="1" ticks="always" tick-size="6" @click="rescale").pr-4
        v-flex(xs12 md6 lg6)
          v-slider(label="Duration:" v-model="durationIndex" thumb-label="always" :max="durationscale" :min="1" @click="bindChart")
  
    bar-chart(v-if="loaded" :chartdata="chartdata" :options="options" :componentkey="componentkey")
    
    div(class="text-center" v-if="!loaded").mt-12
      v-progress-circular(indeterminate class="mb-2" :size="50" :width="5" color="primary")
</template>

<script>
import BarChart from "../BarChart";
import moment from "moment";

export default {
  name: "ParkingTransactions",
  props: {
    slider: Boolean, 
    startyear: Number,
    group: Number,
    duration: Number
  },
  components: { BarChart },
  data: () => ({
    loaded: false,
    chartdata: Object,
    options: Object,
    years: 5,
    timeIndex: Number,
    groupIndex: Number,
    durationIndex: Number,
    componentkey: 0,
    chartType: ["Bar", "Line"]
  }),
  async mounted() {
    this.timeIndex = this.timescale.length - 1;
    this.groupIndex = this.group || 0;
    this.durationIndex = this.duration || 12;
    this.bindChart();
  },
  methods: {
    bindChart() {
      this.getData()
        .then(this.groupData)
        .then(this.sortData)
        .then(this.plotData);
    },

    getData() {
      let skip =
        Math.abs(this.timeIndex - this.timescale.length + 1) * 12 -
        (this.years === this.timeIndex + 1 ? -1 : 12 - this.currentMonth - 1);
      
      return new Promise(resolve => {
        fetch(
          `https://datahub-v2.arlingtonva.us/api/ParkingMeter/ParkingTransactionsByMonth?$skip=${skip}&$top=${this.calculateDuration}&$orderby=monthdate desc`
        ).then(response => {
          resolve(response.json());
        });
      });
    },

    groupData(data) {
      if (this.groupby === "Month")
        //no need to process if monthly since data is already coming by month
        return data;
      else {
        return data.reduce((d, { monthdate, transactionSum }) => {
          let monthyear;
          switch (this.groupby) {
            case "Quarter": 
              monthyear =
                moment
                  .utc(monthdate)
                  .startOf("quarter")
                  .format("MMM") +
                " - " +
                moment
                  .utc(monthdate)
                  .endOf("quarter")
                  .format("MMM YYYY");
              if (!d[monthyear]) {
                d[monthyear] = {
                  monthdate: monthyear,
                  transactionSum: Math.round(transactionSum)
                };
              } else {
                d[monthyear].monthdate = monthyear;
                d[monthyear].transactionSum += Math.round(transactionSum);
              }
              return d;
            case "Year": 
              monthyear = moment.utc(monthdate,"YYYY").format("YYYY");
              if (!d[monthyear]) {
                d[monthyear] = {
                  monthdate: monthyear,
                  transactionSum: Math.round(transactionSum)
                };
              } else {
                d[monthyear].monthdate = monthyear;
                d[monthyear].transactionSum += Math.round(transactionSum);
              }
              return d;
          }
        }, []);
      }
    },

    sortData(data) {
      return Object.values(data).sort(function(a, b) {
        return new Date(a.monthdate) - new Date(b.monthdate);
      });
    },

    plotData(data) {
      this.chartdata = {
        //Data to be represented on x-axis
        labels: [...data.map(d => this.getLabel(d))],
        datasets: [
          {
            label: "Parking Revenue",
            backgroundColor: "#005cbb",
            pointBackgroundColor: "white",
            borderWidth: 3,
            pointBorderColor: "#249EBF",
            fill: false,
            //Data to be represented on y-axis
            data: [...data.map(d => Math.round(d.transactionSum))]
          }
        ]
      };

      this.options = {
        tooltips: {
          callbacks: {
            label: function(tooltipItem, chart) {
              var datasetLabel =
                chart.datasets[tooltipItem.datasetIndex].label || "";
              return (
                datasetLabel +
                ": " +
                new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0
                }).format(tooltipItem.yLabel)
              );
            }
          }
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0
                  }).format(value);
                }
              },
              gridLines: {
                display: true
              },
              scaleLabel: {
                display: true,
                labelString: "Revenue in dollar"
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                display: false
              },
              scaleLabel: {
                display: true,
                labelString: "Period"
              }
            }
          ]
        },
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false
      };
      this.componentkey += 1;
      this.loaded = true;
    },

    rescale() {
      switch (this.groupby) {
        case "Month": 
          this.durationIndex =
            this.timeIndex + 1 === this.years ? this.currentMonth : 12;
          break;
        case "Quarter": 
          this.durationIndex =
            this.timeIndex + 1 === this.years
              ? Math.ceil(this.currentMonth / 4)
              : 4;
          break;
        case "Year": 
          this.durationIndex = this.timeIndex + 1;
          break;
      }
      this.bindChart();
    },

    getLabel(data) {
      switch (this.groupby) {
        case "Month":
          return moment.utc(data.monthdate).format("MMM YYYY");
        case "Quarter":
          //no need to reformat cause its already done during group by operation
          return data.monthdate;
        case "Year":
          return data.monthdate;
      }
    }
  },

  computed: {
    calculateDuration() {
      let duration = Number;
      switch (this.groupby) {
        case "Month": //month
          duration = this.durationIndex;
          break;
        case "Quarter": //quarter
          duration =
            this.timeIndex + 1 === this.years
              ? this.currentMonth -
                Math.floor(this.currentMonth / 3) * 3 +
                (this.durationIndex - 1) * 3
              : this.durationIndex * 3;
          break;
        case "Year": //year
          duration =
            this.timeIndex + 1 === this.years
              ? this.currentMonth + (this.durationIndex - 1) * 12
              : this.durationIndex * 12;
          break;
      }
      return duration;
    },
    durationscale() {
      let max = Number;
      switch (this.groupby) {
        case "Month": //month
          max = (this.timeIndex + 1) * 12;
          break;
        case "Quarter": //quarter
          max = (this.timeIndex + 1) * 3;
          break;
        case "Year": //year
          max = this.timeIndex + 1;
          break;
      }
      return max;
    },
    timescale() {
      let year = this.startyear;
      return [...Array(this.years).keys()].map(() => --year + 1).sort();
    },
    groupscale() {
      return ["Month", "Quarter", "Year"];
    },
    currentMonth() {
      return new Date().getMonth();
    },
    groupby() {
      return this.groupscale[this.groupIndex];
    }
  }
};
</script>